// Typed wrappers for the portable integration modules: the catalogue built from the vendored module manifests and
// the per-organization module instances (decision D19, slice 1); connecting an account (write-only secret), the
// connection test, activation and the egress mode (decision D20, slice 2). Module code never runs in the browser:
// these are plain API calls, and secret values only travel in the PUT body.
import type {
  ActivateModuleInstanceInput,
  CreateModuleInstanceInput,
  IntegrationCatalog,
  IntegrationModuleInstance,
  ModuleEgressMode,
  ModuleScope,
  PatchModuleInstanceInput,
  PutModuleInstanceSecretInput,
} from "@maestros/contracts";
import { api } from "./client.ts";

const enc = encodeURIComponent;
const withSignal = (signal?: AbortSignal) => (signal ? { signal } : {});

// Response shapes of apps/api/src/schemas-modules.ts, not yet in @maestros/contracts (proposed there).
export type ModuleFieldType = "texto" | "segredo" | "url" | "email" | "selecao" | "booleano";

export interface ModuleField {
  key: string;
  label: string;
  type: ModuleFieldType;
  /** Write-only: stored in the vault (PUT .../secret), never returned. */
  secret: boolean;
  required: boolean;
  hint: string | null;
  placeholder: string | null;
  options: { value: string; label: string }[];
  min_length: number | null;
  max_length: number | null;
  hints_by: { field: string; hints: Record<string, string> } | null;
  where_to_find_by: { field: string; steps: Record<string, string[]> } | null;
}

export interface ModuleCardConfiguration {
  module_id: string;
  card_id: string;
  module_version: string | null;
  scope: ModuleScope;
  executable: boolean;
  not_executable_reason: string | null;
  activation: "enabled" | "not_enabled";
  can_test: boolean;
  fields: ModuleField[];
  /** Manifest dependencies of the card; null when not declared (older API answers omit it). */
  dependencies?: ModuleCardDependencies | null;
}

export interface ModuleCardDependencies {
  integrations: { id: string; reason: string | null }[];
  roles: { role: string; min: number; reason: string | null }[];
}

export interface ModuleEgressStatus {
  mode: ModuleEgressMode;
  instance: { id: string; allowlisted: boolean } | null;
}

export interface ModuleInstanceTestResult {
  result: "ok" | "failed";
  summary: string;
  code: string | null;
  instance: IntegrationModuleInstance;
}

/** Every role may read the catalogue. */
export const getModuleCatalog = (signal?: AbortSignal) => api<IntegrationCatalog>("/integration-modules/catalog", withSignal(signal));

/** Maintainers and admins only (the API answers 403 to other roles). */
export const listModuleInstances = (signal?: AbortSignal) => api<{ items: IntegrationModuleInstance[] }>("/integration-module-instances", withSignal(signal));

/** Admin only. The instance is created as a draft; activation is a separate, confirmed action. */
export const createModuleInstance = (body: CreateModuleInstanceInput, idempotencyKey?: string) =>
  api<IntegrationModuleInstance>("/integration-module-instances", { method: "POST", body, ...(idempotencyKey ? { idempotencyKey } : {}) });

/** Admin only; `expected_version` guards against concurrent edits (409 on mismatch). */
export const patchModuleInstance = (id: string, body: PatchModuleInstanceInput) =>
  api<IntegrationModuleInstance>(`/integration-module-instances/${enc(id)}`, { method: "PATCH", body });

/**
 * Admin only. Soft delete: the key is erased, the history kept, and the account disappears from every read. Refused
 * (409 gate_pending, details.cause) when it is active (instance_active) or referenced by a provider connection or by
 * another instance's config (instance_in_use).
 */
export const deleteModuleInstance = (id: string) => api<void>(`/integration-module-instances/${enc(id)}`, { method: "DELETE" });

/** Configuration fields of a card's module (secret fields flagged, never values). */
export const getCardConfiguration = (moduleId: string, cardId: string, signal?: AbortSignal) =>
  api<ModuleCardConfiguration>(`/integration-modules/${enc(moduleId)}/cards/${enc(cardId)}/configuration`, withSignal(signal));

/** Admin only: the installation egress mode and whether this instance is released by the operator. */
export const getModuleEgress = (instanceId?: string | null, signal?: AbortSignal) =>
  api<ModuleEgressStatus>("/integration-modules/egress", { ...withSignal(signal), query: { instance_id: instanceId ?? undefined } });

/** Admin only. Write-only: the value goes to the vault; the answer carries the masked state only. */
export const putModuleInstanceSecret = (id: string, body: PutModuleInstanceSecretInput) =>
  api<IntegrationModuleInstance>(`/integration-module-instances/${enc(id)}/secret`, { method: "PUT", body });

export const deleteModuleInstanceSecret = (id: string) => api<IntegrationModuleInstance>(`/integration-module-instances/${enc(id)}/secret`, { method: "DELETE" });

/** Admin only: runs the module's connection test (a real call when the egress policy allows it). */
export const testModuleInstance = (id: string) => api<ModuleInstanceTestResult>(`/integration-module-instances/${enc(id)}/test`, { method: "POST" });

/** Admin only; `confirm` must be "ATIVAR". Real calls start after activation. */
export const activateModuleInstance = (id: string, body: ActivateModuleInstanceInput) =>
  api<IntegrationModuleInstance>(`/integration-module-instances/${enc(id)}/activate`, { method: "POST", body });

export const deactivateModuleInstance = (id: string, body: { expected_version: number; state?: "disabled" | "draft" }) =>
  api<IntegrationModuleInstance>(`/integration-module-instances/${enc(id)}/deactivate`, { method: "POST", body });
